import { handle400Statuses, sendApiDownloadRequest, sendApiRequest } from './api';
import { docsActionTypes } from '@constants/actionTypes';
import { logNetRequest } from '@utils/usefulFuncs';
import { parseContentDispositionFilename } from '@utils/documentVault';

export const fetchMacroDocs = () => {
  return async (dispatch) => {
    const response = await sendApiRequest('get', 'v1/genai_tickers_filenames?topic=macro', null, null);
    const { status, data } = response;
    handle400Statuses(status);
    if (status === 200 && data && typeof data === 'object') {
      const macroDocs = data['0_FED'] || [];
      if (Array.isArray(macroDocs)) {
        dispatch({
          type: docsActionTypes.setMacroDocs,
          payload: macroDocs,
        });
      }
    }
    return response;
  };
};

export async function fetchTagData(id) {
  const URL = `v1/genai/documents/tags?doc_tag_id=${id}`;
  const { status, data, error } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return { ok: true, data: data[0] };
  }
  return { status, error };
}

/*
  Same endpoint FileDetail edits through. Only the fields passed are sent, so a rename cannot
  accidentally clear a document's tag and vice versa.
*/
export async function updateUserDoc(id, form) {
  const URL = `v1/genai_user_doc/${id}`;
  const { status, data, error } = await sendApiRequest('PATCH', URL, form);
  logNetRequest(URL, status, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, doc: data };
  }
  return { ok: false, status, error: error || 'Something went wrong, please try again.' };
}

export const DOCUMENT_DOWNLOAD_ERRORS = {
  notFound: 'Document not found or not available.',
  cleanFailed: "Couldn't prepare a clean copy. Try again later.",
  generic: 'Something went wrong downloading the file, please try again later.',
};

async function readErrorMessage(response, fallback) {
  try {
    const data = await response.json();
    return data?.error || fallback;
  } catch (e) {
    return fallback;
  }
}

/*
  Single entry point for both download kinds so the two flows cannot drift.

  The clean endpoint is a sibling of the plain one: same auth, same Active Storage ownership
  gate, but the per-recipient watermark is stripped before the bytes stream back. It re-runs the
  full cleaning pipeline on every call behind a 180s server-side read timeout and caches nothing,
  so callers must trigger it on explicit user intent and pass a `signal` to allow cancelation.

  On failure it is deliberately fail-closed: a 502 never falls back to the plain endpoint, which
  would hand back the watermarked file and defeat the entire feature. Offering the watermarked
  original is a decision for the user to make explicitly, in the UI.
*/
export async function downloadUserDoc(blobId, { clean = false, filename, signal } = {}) {
  const parsedBlobId = Number.parseInt(blobId, 10);
  if (!Number.isFinite(parsedBlobId) || parsedBlobId <= 0) {
    // The backend coerces a non-numeric blob id to 0 and answers 400; catch it before the trip.
    return { ok: false, status: 400, error: DOCUMENT_DOWNLOAD_ERRORS.notFound };
  }

  const URL = clean ? `v1/genai_as_user_doc_clean/${parsedBlobId}` : `v1/genai_as_user_doc/${parsedBlobId}`;

  let response;
  try {
    response = await sendApiDownloadRequest(URL, { signal, redirect: 'manual' });
  } catch (e) {
    if (e?.name === 'AbortError') {
      return { ok: false, canceled: true };
    }
    console.error('Error downloading document', e);
    logNetRequest(URL, null, e);
    return { ok: false, error: clean ? DOCUMENT_DOWNLOAD_ERRORS.cleanFailed : DOCUMENT_DOWNLOAD_ERRORS.generic };
  }

  if (!response || typeof response.status !== 'number') {
    // sendApiDownloadRequest swallows its own throw and returns a bare { error }. Test for the
    // type rather than truthiness: an opaque redirect carries a legitimate status of 0.
    return { ok: false, error: response?.error || DOCUMENT_DOWNLOAD_ERRORS.generic };
  }

  const { status } = response;
  logNetRequest(URL, status);

  // With `redirect: 'manual'` an unauthenticated request surfaces as an opaque redirect rather
  // than as the HTML login page, which would otherwise be saved to disk as a "document".
  if (response.type === 'opaqueredirect' || status === 0 || status === 302) {
    handle400Statuses(401);
    return { ok: false, status: 401, error: DOCUMENT_DOWNLOAD_ERRORS.generic };
  }

  handle400Statuses(status);

  if (status === 200) {
    let blob;
    try {
      blob = await response.blob();
    } catch (e) {
      if (e?.name === 'AbortError') {
        return { ok: false, canceled: true };
      }
      console.error('Error reading downloaded document', e);
      return { ok: false, error: DOCUMENT_DOWNLOAD_ERRORS.generic };
    }

    const contentType = response.headers?.get('Content-Type') || blob.type || null;
    const resolvedFilename =
      parseContentDispositionFilename(response.headers?.get('Content-Disposition')) ||
      filename ||
      `document-${parsedBlobId}`;

    return {
      ok: true,
      status,
      blob,
      filename: resolvedFilename,
      contentType,
      // A 200 alone does not prove a cleaning pass ran: the tool is PDF-only and transparently
      // returns the untouched original for anything else.
      wasCleaned: clean && contentType === 'application/pdf',
    };
  }

  if (status === 400) {
    return { ok: false, status, error: await readErrorMessage(response, DOCUMENT_DOWNLOAD_ERRORS.notFound) };
  }

  if (status === 502) {
    return { ok: false, status, error: await readErrorMessage(response, DOCUMENT_DOWNLOAD_ERRORS.cleanFailed) };
  }

  return {
    ok: false,
    status,
    error: await readErrorMessage(
      response,
      clean ? DOCUMENT_DOWNLOAD_ERRORS.cleanFailed : DOCUMENT_DOWNLOAD_ERRORS.generic
    ),
  };
}

export const fetchDocs = async (params = '') => {
  const URL = `v1/genai/documents${params}`;
  const { status, data, error } = await sendApiRequest('get', URL);
  handle400Statuses(status);
  logNetRequest(URL, status, data);
  if (status === 200) {
    const docs = data.documents;
    return docs;
  } else {
    console.error('Error fetching docs', error);
    return null;
  }
};
